/**
 * Section « Publier sur les réseaux » de l'éditeur d'article (admin).
 *
 * Visible uniquement quand l'article est publié. Un bouton « Générer le
 * carrousel » (partagé LinkedIn + Instagram) + une ligne INDÉPENDANTE par réseau
 * (LinkedIn / Facebook / Instagram) : connecter (OAuth) ou publier en 1 clic,
 * avec l'état de la dernière publication (`blog_social_posts`).
 *
 * Mobile-first : boutons pleine largeur, cibles ≥ 44 px, retour tactile.
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Linkedin,
  Facebook,
  Instagram,
  Sparkles,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import type { BlogCarouselCache, BlogCarouselSlide } from '@jeraisonne/db';
import { useSocialAccount } from '@/lib/admin/youtube-queries';
import {
  useBlogSocialPosts,
  usePostBlogSocial,
  useRenderBlogCarousel,
  useStartSocialOAuth,
  type BlogSocialPostRow,
} from '@/lib/admin/use-social';
import type { SocialNetwork, SocialProvider } from '@/lib/admin/social-client';

interface NetworkDef {
  platform: SocialNetwork;
  /** Fournisseur OAuth : Meta couvre Facebook + Instagram. */
  provider: SocialProvider;
  label: string;
  Icon: LucideIcon;
  /** LinkedIn & Instagram publient un carrousel d'images. */
  usesCarousel: boolean;
}

const NETWORKS: NetworkDef[] = [
  {
    platform: 'linkedin',
    provider: 'linkedin',
    label: 'LinkedIn',
    Icon: Linkedin,
    usesCarousel: true,
  },
  {
    platform: 'facebook',
    provider: 'meta',
    label: 'Facebook',
    Icon: Facebook,
    usesCarousel: false,
  },
  {
    platform: 'instagram',
    provider: 'meta',
    label: 'Instagram',
    Icon: Instagram,
    usesCarousel: true,
  },
];

/** Libellé court d'une diapo dans l'aperçu (cover / point clé / sondage). */
function slideLabel(kind: BlogCarouselSlide['kind'], index: number): string {
  if (kind === 'cover') return 'Cover';
  if (kind === 'poll') return 'Sondage';
  return `Point clé ${index}`;
}

/** Aperçu d'un jeu de slides au format de la plateforme (portrait IG / carré LinkedIn). */
function CarouselFormatPreview({
  title,
  slides,
  portrait,
}: {
  title: string;
  slides: BlogCarouselSlide[];
  portrait?: boolean;
}) {
  if (!slides.length) return null;
  return (
    <div>
      <p className="text-ink-500 mb-1.5 font-sans text-[11px] font-semibold">{title}</p>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {slides.map((s, i) => (
          <a
            key={i}
            href={s.url}
            target="_blank"
            rel="noreferrer"
            title="Ouvrir l'image en grand"
            className="group relative shrink-0 active:scale-[0.98]"
          >
            <img
              src={s.url}
              alt={slideLabel(s.kind, i)}
              className={`border-ink-200 rounded-xl border object-cover transition-transform group-hover:scale-[1.02] ${
                portrait ? 'h-56 w-[179px]' : 'h-44 w-44 sm:h-52 sm:w-52'
              }`}
            />
            <span className="bg-ink-900/70 absolute left-1.5 top-1.5 rounded-full px-2 py-0.5 font-sans text-[11px] font-semibold text-white">
              {slideLabel(s.kind, i)}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

export function SocialPublishSection({
  articleId,
  isPublished,
  initialCache,
}: {
  articleId: string;
  isPublished: boolean;
  initialCache?: BlogCarouselCache | null;
}) {
  const { data: posts = [] } = useBlogSocialPosts(articleId);
  const render = useRenderBlogCarousel(articleId);
  const [carousels, setCarousels] = useState<{
    instagram: BlogCarouselSlide[];
    linkedin: BlogCarouselSlide[];
  }>(() => ({
    instagram: initialCache?.instagram?.slides ?? [],
    linkedin: initialCache?.linkedin?.slides ?? [],
  }));
  const [progress, setProgress] = useState<{ label: string; done: number; total: number } | null>(
    null,
  );

  if (!isPublished) {
    return (
      <div className="space-y-3">
        <p className="text-ink-500 font-sans text-[13px]">
          Publie d&apos;abord l&apos;article pour pouvoir le partager sur les réseaux.
        </p>
        <SettingsLink />
      </div>
    );
  }

  const postByPlatform = new Map(posts.map((p) => [p.platform, p]));

  // Un slide par invocation Edge (rendu image lourd → 1 budget compute / slide) :
  // on boucle Instagram puis LinkedIn, en affichant les slides au fur et à mesure.
  async function generateCarousel() {
    await render.mutateAsync({
      platform: 'instagram',
      onProgress: (slides, done, total) => {
        setCarousels((c) => ({ ...c, instagram: slides }));
        setProgress({ label: 'Instagram', done, total });
      },
    });
    await render.mutateAsync({
      platform: 'linkedin',
      onProgress: (slides, done, total) => {
        setCarousels((c) => ({ ...c, linkedin: slides }));
        setProgress({ label: 'LinkedIn', done, total });
      },
    });
    setProgress(null);
  }

  const hasSlides = carousels.instagram.length > 0 || carousels.linkedin.length > 0;
  const busyLabel = progress
    ? `${progress.label} ${progress.done}/${progress.total}…`
    : 'Génération…';

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <SettingsLink />
      </div>

      {/* Carrousel partagé LinkedIn + Instagram */}
      <div className="border-ink-100 bg-ink-50/40 rounded-xl border p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="font-display text-ink-700 text-[12px] font-semibold">
            Carrousel (LinkedIn + Instagram)
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="flex items-center gap-1.5"
            disabled={render.isPending}
            onClick={generateCarousel}
          >
            <Sparkles className="h-4 w-4" />
            {render.isPending ? busyLabel : hasSlides ? 'Régénérer' : 'Générer le carrousel'}
          </Button>
        </div>
        {render.isError && (
          <p className="text-accent-600 mt-2 font-sans text-[12px]">{render.error?.message}</p>
        )}
        {hasSlides && (
          <div className="mt-3 space-y-4">
            <CarouselFormatPreview
              title="Instagram — portrait 4:5"
              slides={carousels.instagram}
              portrait
            />
            <CarouselFormatPreview title="LinkedIn — carré 1:1" slides={carousels.linkedin} />
            <p className="text-ink-400 font-sans text-[11px]">
              Touche une diapo pour l&apos;ouvrir en plein format. Chaque réseau a son format dédié.
            </p>
          </div>
        )}
      </div>

      {/* Une ligne indépendante par réseau */}
      {NETWORKS.map((net) => (
        <NetworkRow
          key={net.platform}
          articleId={articleId}
          net={net}
          post={postByPlatform.get(net.platform)}
        />
      ))}
    </div>
  );
}

function SettingsLink() {
  return (
    <Link
      to="/tutor/admin/social/settings"
      className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 font-sans text-[12px] font-semibold"
    >
      <Settings className="h-3.5 w-3.5" />
      Configurer les accès
    </Link>
  );
}

function StatusBadge({ post }: { post: BlogSocialPostRow | undefined }) {
  if (!post || post.status === 'idle') return null;
  if (post.status === 'posting') {
    return (
      <span className="text-ink-500 inline-flex items-center gap-1 font-sans text-[12px]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Publication…
      </span>
    );
  }
  if (post.status === 'posted') {
    return (
      <a
        href={post.external_url ?? '#'}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 font-sans text-[12px] font-semibold text-emerald-700 hover:underline"
      >
        <CheckCircle2 className="h-3.5 w-3.5" /> Voir le post <ExternalLink className="h-3 w-3" />
      </a>
    );
  }
  // failed
  return (
    <span className="text-accent-600 inline-flex items-start gap-1 font-sans text-[12px]">
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      {post.error ?? 'Échec'}
    </span>
  );
}

function NetworkRow({
  articleId,
  net,
  post,
}: {
  articleId: string;
  net: NetworkDef;
  post: BlogSocialPostRow | undefined;
}) {
  const { data: account, isLoading } = useSocialAccount(net.platform);
  const startOAuth = useStartSocialOAuth();
  const postMut = usePostBlogSocial(articleId);
  const { Icon } = net;

  async function connect() {
    const { url } = await startOAuth.mutateAsync(net.provider);
    window.location.href = url;
  }

  return (
    <div className="border-ink-200 rounded-xl border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-display text-ink-900 inline-flex items-center gap-2 text-[14px] font-bold">
          <Icon className="h-4 w-4" />
          {net.label}
          {account?.channel_title && (
            <span className="text-ink-400 text-[12px] font-normal">· {account.channel_title}</span>
          )}
        </span>

        {isLoading ? (
          <Loader2 className="text-ink-400 h-4 w-4 animate-spin" />
        ) : account ? (
          <Button
            type="button"
            size="sm"
            className="min-w-32 flex-1 sm:flex-none"
            disabled={postMut.isPending}
            onClick={() => postMut.mutate(net.platform)}
          >
            {postMut.isPending ? 'Publication…' : `Publier sur ${net.label}`}
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="min-w-32 flex-1 sm:flex-none"
            disabled={startOAuth.isPending}
            onClick={connect}
          >
            {startOAuth.isPending ? 'Redirection…' : 'Connecter'}
          </Button>
        )}
      </div>

      {/* État de la dernière publication / erreur immédiate */}
      <div className="mt-2 min-h-5">
        {postMut.isError ? (
          <span className="text-accent-600 font-sans text-[12px]">{postMut.error?.message}</span>
        ) : (
          <StatusBadge post={post} />
        )}
      </div>
    </div>
  );
}
